import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Player } from '@lordicon/react';
import Layout from '../../Layout/Layout';
import TitleBar from '../../Layout/TitleBar';
import {
  useTranslator,
  useAppContext,
  withLoading,
  withMobileAppMode,
  callNativeMobileApp,
} from '@front-activerse/common-components';
import {
  Container,
  Row,
  Col,
  NavItem,
  Form,
  Input,
  InputGroupText,
  Button,
  Modal,
} from 'reactstrap';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import UniversalPlaceholder from '../../Layout/UniversalPlaceholder';
import UserMeal from './UserMeal';

interface IUserMealsIndexContainerProps {
  mealsNavigation: {
    path: string;
    translationKey: string;
  }[];
  userMeals: any[];
  aiAccess: boolean;
  dietAccess: boolean;
  mobileAppMode: boolean;
}

const UserMealsIndexContainer: React.FC<IUserMealsIndexContainerProps> = ({
  mealsNavigation,
  userMeals,
  aiAccess,
  dietAccess,
  mobileAppMode,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslator();
  const [searchValue, setSearchValue] = useState('');

  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    sessionStorage.setItem('aiAccess', aiAccess ? 'true' : 'false');
    sessionStorage.setItem('dietAccess', dietAccess ? 'true' : 'false');
  }, [aiAccess, dietAccess]);

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  const {
    images: {
      addToTrackerIcon,
      describeMealIcon,
      scanFoodIcon,
      scanBarcodeIcon,
      aiBadgeTiny,
      searchButton,
      noMealsIcon1,
      noMealsIcon2,
      noMealsIcon3,
      createCustomMealIcon,
    },
    lotties: { searchIcon },
  } = useAppContext();

  const navRef = useRef<HTMLUListElement | null>(null);
  const playerRefSearch = useRef<Player>(null);
  const iconsIconMiddleColor = useMemo(() => {
    if (typeof document === 'undefined') return '#000000';
    const color = getComputedStyle(document.documentElement)
      .getPropertyValue('--icons-icon-middle-color')
      .trim();
    return color || '#000000';
  }, []);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (searchValue) {
      navigate(`/meals/user-meals/search?q=${searchValue}`);
    }
  };

  useEffect(() => {
    const scrollToEnd = () => {
      if (navRef.current) {
        navRef.current.scrollLeft = navRef.current.scrollWidth;
      }
    };

    // Use requestAnimationFrame to ensure DOM is fully rendered
    requestAnimationFrame(() => {
      scrollToEnd();
      // Also try after a small delay to handle async rendering
      setTimeout(scrollToEnd, 0);
    });
  }, []);

  const handleAddToYourMeals = () => {
    if (mobileAppMode) {
      callNativeMobileApp({
        action: 'open_bottom_sheet',
        payload: {
          type: 'user_meals_log',
        },
      });
    } else {
      setShowModal(true);
    }
  };

  const renderUserMeals = () => {
    if (userMeals.length > 0) {
      return (
        <Row className="mealplan-fav-meals full-height5">
          {userMeals.map((meal) => (
            <Col xs="6" md="3" key={meal.id}>
              <UserMeal meal={meal} />
            </Col>
          ))}
        </Row>
      );
    }

    return (
      <div className="full-height5 d-flex align-items-center justify-content-center">
        <div className="text-center">
          <h2 className="title-medium semi-bold mb-m">{t('yourMeal.noMeals.title')}</h2>
          <p className="text-text-variant-color mb-xl">{t('yourMeal.noMeals.content')}</p>

          <div className="no-meals-box box-1">
            <img src={noMealsIcon1} alt={t('yourMeal.noMeals.highlightItem1.title')} />
            <div className="text-start">
              <p className="small semi-bold text-text-main-color mb-xxs">
                {t('yourMeal.noMeals.highlightItem1.title')}
              </p>
              <p className="small text-text-variant-color mb-0">
                {t('yourMeal.noMeals.highlightItem1.content')}
              </p>
            </div>
          </div>

          <div className="no-meals-box box-2">
            <img src={noMealsIcon2} alt={t('yourMeal.noMeals.highlightItem2.title')} />
            <div className="text-start">
              <p className="small semi-bold text-text-main-color mb-xxs">
                {t('yourMeal.noMeals.highlightItem2.title')}
              </p>
              <p className="small text-text-variant-color mb-0">
                {t('yourMeal.noMeals.highlightItem2.content')}
              </p>
            </div>
          </div>

          <div className="no-meals-box box-3 mb-0">
            <img src={noMealsIcon3} alt={t('yourMeal.noMeals.highlightItem3.title')} />
            <div className="text-start">
              <p className="small semi-bold text-text-main-color mb-xxs">
                {t('yourMeal.noMeals.highlightItem3.title')}
              </p>
              <p className="small text-text-variant-color mb-0">
                {t('yourMeal.noMeals.highlightItem3.content')}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Layout>
        <TitleBar showBackBtn={false}>{t('meals.navbarTitle')}</TitleBar>
        <div className="page-template-user-meals">
          <Container className="py-md-4">
            <Row>
              <Col xl={{ size: 10, offset: 1 }}>
                <div className="page-container">
                  {mealsNavigation && mealsNavigation.length > 0 && (
                    <div className="sub-navigation text-center">
                      <Container>
                        <ul className="d-block nav" ref={navRef}>
                          {mealsNavigation.map((item) => (
                            <NavItem key={item.path}>
                              <NavLink to={item.path} replace={true} className="nav-link">
                                {t(item.translationKey)}
                              </NavLink>
                            </NavItem>
                          ))}
                        </ul>
                      </Container>
                    </div>
                  )}

                  {/* search */}
                  {userMeals.length > 0 && (
                    <Row>
                      <Col md={{ size: 8, offset: 2 }}>
                        <div className="search-container">
                          <Form onSubmit={handleSubmit}>
                            <div
                              className="input-group-with-addon start"
                              onMouseEnter={() => playerRefSearch.current?.playFromBeginning()}
                              onTouchStart={() => playerRefSearch.current?.playFromBeginning()}
                            >
                              <InputGroupText>
                                <Player
                                  ref={playerRefSearch}
                                  icon={searchIcon}
                                  size={24}
                                  colorize={iconsIconMiddleColor}
                                />
                              </InputGroupText>
                              <Input
                                id="search"
                                name="search"
                                type="search"
                                enterkeyhint="search"
                                value={searchValue}
                                onChange={(e) => setSearchValue(e.target.value)}
                                placeholder={t('meal.search.inputPlaceholder')}
                              />
                              <Button type="submit" color="primary" size="sm" className="ms-2 px-m">
                                <img src={searchButton} alt="search" />
                              </Button>
                            </div>
                          </Form>
                        </div>
                      </Col>
                    </Row>
                  )}

                  <div className="fav-result-container">{renderUserMeals()}</div>
                </div>
              </Col>
            </Row>
          </Container>
          <div className="sticky-footer">
            <Container className="px-3 text-center">
              <Row>
                <Col xs="12" md={{ size: 4, offset: 4 }}>
                  <Button
                    color="tracker-animated"
                    className="w-100"
                    size="sm"
                    onClick={() => handleAddToYourMeals()}
                  >
                    <span>
                      <img
                        src={addToTrackerIcon}
                        alt={t('tracker.meal.addToTrackerButton')}
                        className="me-2"
                      />
                      {userMeals.length > 0
                        ? t('yourMeal.addButton')
                        : t('yourMeal.noMeals.addButton')}
                    </span>
                  </Button>
                </Col>
              </Row>
            </Container>
          </div>
        </div>
      </Layout>

      <Modal
        className="from-bottom"
        isOpen={showModal}
        fullscreen={true}
        size="lg"
        toggle={toggleModal}
      >
        <div className="select-insert-date">
          <p className="mb-0">{t('actionSheet.logOwnMeal.title')}</p>

          <Container>
            <Row>
              <Col xs="12" md={{ size: 4, offset: 4 }}>
                <Button
                  color="tracker"
                  className="w-100 meal"
                  tag={Link}
                  to={`/meals/user-meals/scan-plate`}
                  onClick={toggleModal}
                >
                  <div className="d-flex align-items-center justify-content-between w-100">
                    <div>
                      <img
                        src={scanFoodIcon}
                        alt={t('actionSheet.logToTracker.option.scan.food')}
                      />
                      {t('actionSheet.logToTracker.option.scan.food')}
                    </div>
                    <div className="text-end">
                      {!aiAccess && (
                        <span className="me-1">{t('actionSheet.label.getAccess')}</span>
                      )}
                      <img src={aiBadgeTiny} alt="" className="ai-badge" />
                    </div>
                  </div>
                </Button>

                <Button
                  color="tracker"
                  className="w-100 meal"
                  tag={Link}
                  to={`/meals/user-meals/describe-meal`}
                  onClick={toggleModal}
                >
                  <div className="d-flex align-items-center justify-content-between w-100">
                    <div>
                      <img
                        src={describeMealIcon}
                        alt={t('actionSheet.logToTracker.option.describeMeal')}
                      />
                      {t('actionSheet.logToTracker.option.describeMeal')}
                    </div>
                    <div className="text-end">
                      {!aiAccess && (
                        <span className="me-1">{t('actionSheet.label.getAccess')}</span>
                      )}
                      <img src={aiBadgeTiny} alt="" className="ai-badge" />
                    </div>
                  </div>
                </Button>

                <Button
                  color="tracker"
                  className="w-100 meal"
                  tag={Link}
                  to={`/meals/user-meals/scan-barcode`}
                  onClick={toggleModal}
                >
                  <div className="d-flex align-items-center justify-content-between w-100">
                    <div>
                      <img
                        src={scanBarcodeIcon}
                        alt={t('actionSheet.logToTracker.option.scan.barcode')}
                      />
                      {t('actionSheet.logToTracker.option.scan.barcode')}
                    </div>
                  </div>

                  <div className="text-end">
                    {!aiAccess && <span className="me-1">{t('actionSheet.label.getAccess')}</span>}
                    <img src={aiBadgeTiny} alt="" className="ai-badge" />
                  </div>
                </Button>

                <Button
                  color="tracker"
                  tag={Link}
                  to="/meals/user-meals/describe-manual-meal"
                  className="w-100 meal"
                  onClick={toggleModal}
                >
                  <div>
                    <img
                      src={createCustomMealIcon}
                      alt={t('actionSheet.logToTracker.option.createCustomMeal')}
                    />
                    {t('actionSheet.logToTracker.option.createCustomMeal')}
                  </div>
                  <div className="text-end">
                    {/* <span className="mt-1 new">{t('actionSheet.label.new')}</span> */}
                  </div>
                </Button>
              </Col>
            </Row>
          </Container>
        </div>
      </Modal>
    </>
  );
};

const Placeholder = () => <UniversalPlaceholder titleBar="meals.navbarTitle" />;

export default withLoading(withMobileAppMode(UserMealsIndexContainer), Placeholder);
